import { uploadErrorLog } from '@/api/error';
import { IPlayErrorCtx, ISecureMediaFallbackExtraCtx } from '@/interface/errorUpload';
import store from '@/store';
import { IGetMediaEncryptInfoResponse } from '@/types/sw';
import { initSessionId, getSessionIdFromStore } from '@/utils/sessionId';
import { sendMessageToSW } from '@/utils/sw';
import { swReadyState } from '@/service/secureMedia';
import { Async, Object as objUtils } from '@reolink-web/utils';
import { AxiosError } from 'axios';

class ErrorUploader {

    private sessionId: string | null = null;

    /**
     * 初始化 session id
     */
    public async initSessionId(): Promise<string> {

        this.sessionId = await initSessionId();
        return this.sessionId;
    
    }

    /**
     * 获取当前 session id，如果未初始化则先初始化
     */
    public async getSessionId(): Promise<string> {

        if ( !this.sessionId ) {

            await this.initSessionId();
        
        }
        return this.sessionId!;
    
    }

    /**
     * 从 IndexedDB 获取 session id（供 Service Worker 使用）
     */
    public static getSessionIdFromStore = getSessionIdFromStore;

    /**
     * 从 Service Worker 获取指定 URL 的加密信息。
     * SW 不可用时直接返回 false，避免拖慢 / 阻塞错误上报本身。
     */
    private async fetchMediaEncryptInfo( url: string ): Promise<IGetMediaEncryptInfoResponse> {

        if ( !swReadyState.isReady ) {

            return {
                isUrlEncryption: false,
                isE2eeEncryption: false
            };

        }

        try {

            const message = {
                type: 'getMediaEncryptInfo',
                payload: { url }
            };
            return await sendMessageToSW<typeof message, IGetMediaEncryptInfoResponse>( message );

        } catch ( error ) {

            console.warn( '[ErrorUploader] Failed to get encrypt info from SW:', error );
            return {
                isUrlEncryption: false,
                isE2eeEncryption: false
            };

        }

    }

    /**
     * 构建错误上下文
     */
    private async buildErrorCtx( url: string, type: IPlayErrorCtx['type'] ): Promise<IPlayErrorCtx> {

        const encryptInfo = await this.fetchMediaEncryptInfo( url );
        return {
            supportServiceWorker: 'serviceWorker' in navigator,
            isUrlEncryption: encryptInfo.isUrlEncryption,
            isE2eeEncryption: encryptInfo.isE2eeEncryption,
            type,
            userId: store.getters.userId,
            userAgent: navigator.userAgent
        };

    }

    /**
     * 上报视频播放错误（解码失败等）
     */
    public uploadVideoPlayError( videoUrl: string, error: unknown ): void {

        Async.invokeAsync( async () => {

            const ctx = await this.buildErrorCtx( videoUrl, 'playFailed' );
            await uploadErrorLog({
                level: 'high',
                error,
                message: 'Error in playing video',
                ctx
            });

        });

    }

    /**
     * 上报"主线程降级解密失败"错误。
     *
     * 触发场景：SW 不可用而走 `loadSecureMediaAsObjectUrl` 兜底，
     * 但 fetch / 解密 / Blob 生成任一阶段抛错。指令与播放器统一调用此方法，
     * 便于后端按 `secureMediaFallbackFailed` 维度聚合分析。
     */
    public uploadSecureMediaFallbackError(
        url: string,
        error: unknown,
        extra?: ISecureMediaFallbackExtraCtx
    ): void {

        Async.invokeAsync( async () => {

            const ctx = {
                ...await this.buildErrorCtx( url, 'secureMediaFallbackFailed' ),
                ...extra
            };
            await uploadErrorLog({
                level: 'middle',
                error,
                message: 'Error in secure media fallback',
                ctx
            });

        });

    }
    
    public uploadCancelPlanError( e: unknown ): void {

        Async.invokeAsync( async () => {

            let errorText: string;
            if (( e as AxiosError )?.response ) {

                const response = ( e as AxiosError ).response;
                errorText = JSON.stringify({
                    details: `status: ${response?.status ?? 'unknown'}, data: ${JSON.stringify( response?.data )}`
                });
            
            } else {

                errorText = JSON.stringify( e );
            
            }

            await uploadErrorLog({
                level: 'high',
                error: errorText,
                message: 'Error in canceling plan',
                ctx: {
                    userId: store.getters.userId,
                    userAgent: navigator.userAgent
                }
            });

        });

    }

    /**
     * 过滤对象中的敏感字段（仅处理第一层）
     */
    private filterPrivacyData<T extends Record<string, unknown>>( obj: T, privacyFields?: string[] ): Partial<T> {

        if ( !privacyFields || privacyFields.length === 0 ) {

            return obj;
        
        }

        const result = objUtils.deepClone( obj );
        for ( const field of privacyFields ) {

            delete result[ field ];
        
        }
        return result;

    }

    /**
     * 上报接口错误
     * @param apiName 接口名称
     * @param e 错误对象
     * @param level 错误级别，默认为 'middle'
     * @param privacyData 敏感字段列表，这些字段的值将被过滤
     */
    public uploadApiError( apiName: string, e: unknown, level: 'high' | 'middle' | 'low' = 'middle', privacyData?: string[] ): void {

        Async.invokeAsync( async () => {

            let errorText: string;
            if (( e as AxiosError )?.response ) {

                const response = ( e as AxiosError ).response;
                const requestConfig = ( e as AxiosError ).config;
                
                // 过滤响应数据和请求数据中的敏感信息
                const filteredResponseData = this.filterPrivacyData( response?.data ?? {}, privacyData );
                const filteredRequestData = this.filterPrivacyData(
                    typeof requestConfig.data === 'string' ? JSON.parse( requestConfig.data ) : requestConfig.data ?? {},
                    privacyData
                );
                
                errorText = JSON.stringify({
                    details: `status: ${response?.status ?? 'unknown'}, data: ${JSON.stringify( filteredResponseData )}`,
                    requestData: `headers: ${JSON.stringify( Object.keys( requestConfig.headers ))}, data: ${JSON.stringify( filteredRequestData )}`,
                });
            
            } else if ( e instanceof Error ) {

                errorText = JSON.stringify({
                    name: e.name,
                    message: e.message,
                    stack: e.stack
                });
            
            } else {

                errorText = JSON.stringify( e );
            
            }

            await uploadErrorLog({
                level,
                error: errorText,
                message: `API Error: ${apiName}`,
                ctx: {
                    userId: store.getters.userId,
                    userAgent: navigator.userAgent,
                    apiName,
                    url: location.href
                }
            });

        });

    }
    
    public uploadCommonError( name: string, e: unknown, level: 'high' | 'middle' | 'low' = 'middle' ): void {

        this.uploadApiError( name, e, level );

    }

}

export const errorUploader = new ErrorUploader();
